/* eslint-disable no-case-declarations */
import { useLazyQuery, useMutation } from "@apollo/client";
import { Grid, Typography, useMediaQuery } from "@material-ui/core";
import { EmptyList } from "components/PaginationList/Styles";
import SearchBox from "components/SearchBox";
import { Slider } from "components/Slider/Slider";
import Table from "components/Table";
import { MenuActions } from "components/Table/MenuActions";
import { EmptyTableContent } from "components/Table/NothingDisplay";
import { ROW_PER_PAGE_OPTIONS } from "config";
import { theme } from "config/theme";
import useText from "constants/text";
import { useAlertDispatch } from "contexts/AlertContext";
import { NewRequestQuoteContext } from "contexts/RequestQuoteContext";
import { useValidationDialogStyle } from "domains/CashManagement/style";
import { useRequestRowColumns } from "domains/Request/data";
import { acceptRequest, declineRequest } from "domains/Request/graphql/mutation";
import { GetClientRequests } from "domains/Request/graphql/queries";
import { Dispatcher, ERequestStatus, ERequestType, Request, RequestDocument } from "generated/graphql";
import { useCurrentClient } from "hooks";
import _ from "lodash";
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "ui/Button";
import Dialog from "ui/Dialog";
import { TextField } from "ui/TextField";
import { RequestListContainer } from "../shared/MainStyles";
import RightSideDetailsRequest from "../shared/RightSideDetailsRequest";
import { formatProduct } from "../shared/utils";
import { defaultRequestsActions, RequestRejectionData, requestRejectionInitData, requestsActions } from "./data";
import RequestFilter from "./RequestFilter";
import { useStyles } from "./styles";

export default function RequestList() {
  const { setRequestQuote, setRequestQuoteDetails, setIsEditedNT, setRequestQuoteNT, setRequestQuoteTour } =
    React.useContext(NewRequestQuoteContext);
  const { client } = useCurrentClient();
  const styles = useStyles();
  const classes = useValidationDialogStyle();
  const { text } = useText();
  const [filters, setFilters] = React.useState(new Map<string, any[]>());
  const [dateFilter, setDateFilter] = React.useState(null);
  const [allRequests, setAllRequests] = React.useState<Request[]>(null);
  const requestActions = requestsActions(text);
  const requestDefaultActions = defaultRequestsActions(text);
  const [selectedRequest, setSelectedRequest] = React.useState<Request>(null);
  const [currentRow, setCurrentRow] = React.useState<Request>(null);
  const [currentKey, setCurrentKey] = React.useState<string>(null);
  const [acceptRequestForClient] = useMutation(acceptRequest);
  const [declineRequestForClient] = useMutation(declineRequest);
  const dispatchAlert = useAlertDispatch();
  const [countTotal, setCountTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [confirm, setConfirm] = React.useState(false);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const columns = useRequestRowColumns();
  const [isAccept, setIsAccept] = React.useState(false);
  const [rejectionData, setRejectionData] = React.useState<RequestRejectionData>(requestRejectionInitData);
  const navigate = useNavigate();
  const [requestsByClient, { loading }] = useLazyQuery(GetClientRequests, {
    onCompleted: (data) => {
      setCountTotal(data.allRequestsByClient?.countTotal);
      const {
        allRequestsByClient: { data: requests },
      } = data;
      setAllRequests(requests);
    },
    fetchPolicy: "cache-and-network",
  });
  const getRequests = () => {
    if (client) {
      requestsByClient({
        variables: {
          queries: {
            page: page + 1,
            limit: rowsPerPage,
            id: client?._id,
            status: filters.get("status") || [],
            type: filters.get("mission") || [],
          },
        },
      });
    }
  };

  const handleInputChange = (e: any) => {
    const { name, value } = e.target;
    setRejectionData((prev) => {
      return {
        ...prev,
        [name]: value,
      };
    });
  };

  React.useEffect(() => {
    getRequests();
    // eslint-disable-next-line
  }, [client, filters, page, rowsPerPage]);

  const getFilter = (filter: string, options: any[] = []) => {
    setFilters((map) => new Map(map.set(filter, options)));
  };
  const getDateFilter = (from: string, to: string) => {
    setDateFilter({ from, to });
  };
  const onChangePage = (event: React.ChangeEvent<{}>, newValue: number) => {
    setPage(newValue);
  };

  const acceptRequestHandler = () => {
    try {
      acceptRequestForClient({
        variables: {
          input: { id: currentRow?._id },
        },
      })
        .then((response) => {
          setConfirm(false);
          const {
            data: { acceptRequest },
          } = response;
          if (acceptRequest?.code === 200) {
            setRequestQuoteDetails(acceptRequest?.data);
            dispatchAlert({
              message: text.requestQuote.accept_request,
              severity: "success",
            });
          } else {
            return dispatchAlert({
              message: acceptRequest?.message || "",
              severity: "error",
            });
          }
        })
        .catch((err: any) => {
          setConfirm(false);
        });
    } catch (error) {
      setConfirm(false);
    }
  };

  const declineRequestHandler = () => {
    try {
      declineRequestForClient({
        variables: {
          input: {
            id: currentRow?._id,
            declineReason: rejectionData.reason,
          },
        },
      })
        .then((response) => {
          setConfirm(false);
          const {
            data: { declineRequest },
          } = response;
          if (declineRequest?.code === 200) {
            setRequestQuoteDetails(declineRequest?.data);
            dispatchAlert({
              message: text.requestQuote.decline_request,
              severity: "success",
            });
          } else {
            return dispatchAlert({
              message: declineRequest?.message || "",
              severity: "error",
            });
          }
        })
        .catch((err) => {
          setConfirm(false);
        });
    } catch (error) {
      setConfirm(false);
    }
  };

  const handleSubmit = (row: Request, key: string) => {
    setConfirm(true);
    setCurrentRow(row);
    setCurrentKey(key);
    if (key === RequestAction.ACCEPT.label) {
      setIsAccept(true);
    } else {
      setIsAccept(false);
    }
  };
  const handleConfirmRequest = () => {
    if (currentKey === RequestAction.ACCEPT.label) {
      acceptRequestHandler();
    } else {
      declineRequestHandler();
    }
  };

  const formatStops = (value: any) =>
    value.stops.map(
      (stop: { action: any; address: { place_id: any }; itemsToPick: any[]; itemsToDelivered: any[] }, index: any) => {
        return {
          action: stop.action,
          address: { place_id: stop.address.place_id },
          itemsToPick: stop.itemsToPick?.flatMap((it) => ({
            counter: it.counter,
            product: formatProduct(it.product),
          })),
          itemsToDelivered: stop.itemsToDelivered?.flatMap((it) => ({
            counter: it.counter,
            product: formatProduct(it.product),
          })),
          position: index,
        };
      }
    );

  const handleEdit = (request: any) => {
    switch (request.type) {
      case ERequestType.Mad:
        setIsEditedNT(true);
        setRequestQuote({
          ...request,
          handler: _.omit(request?.handler, ["__typename"]),
          dispatcher: _.omit(request?.dispatcher, ["__typename"]),
          vehicles: request?.vehicles.map((vehicle: any) => _.omit(vehicle, ["__typename"])),
        });
        navigate("/tracking/request/samapapser");
        break;

      case ERequestType.Tr:
        const templateAxes = request?.templateAxes.map((axe: any) => ({
          name: axe?.name,
          price: axe?.price,
          reccurenceType: axe.reccurenceType,
          slotEnd: axe.slotEnd,
          slotStart: axe.slotStart,
          vehicles: axe.vehicles.map((vehicle: any) => ({
            vehicleType: vehicle.vehicleType,
            numberOfVehicule: vehicle.numberOfVehicule,
          })),
          templateStops: axe.templateStops.map((stop: any) => ({
            address: stop.address,
          })),
        }));

        setIsEditedNT(true);
        setRequestQuoteTour({
          ...request,
          templateAxes: templateAxes,
        });
        navigate("/tracking/request/tour");
        break;

      case ERequestType.Nt:
        const stops = request?.templateStops;
        const { templateStops, reccurenceType, slotEnd, reccurenceOptions, slotStart, ...req } = request;
        const formatedRequest = { stops, ...req };
        formatedRequest?.stops?.length &&
          setRequestQuoteNT({
            ...formatedRequest,
            vehicles: formatedRequest?.vehicles.map((vehicle: any) => _.omit(vehicle, ["__typename"])),
          });
        setIsEditedNT(true);
        navigate("/national-transport");
        break;

      default:
    }
  };
  const isCurrentRow = (data: Request) => data?._id === selectedRequest?._id;

  const onRowMenuClick = (key: string, row: any) => {
    switch (key) {
      case RequestAction.EDIT.label:
        // eslint-disable-next-line no-alert
        return handleEdit(row);
      case RequestAction.DELETE.label:
        // eslint-disable-next-line no-alert
        return alert(row);
      case RequestAction.ACCEPT.label:
        return handleSubmit(row, key);
      case RequestAction.DECLINE.label:
        return handleSubmit(row, key);
      // no default
    }
  };

  const [searchTerm, setSearchTerm] = React.useState("");
  const [openSlider, setOpenSlider] = React.useState(false);

  const handleSearchTerm = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(event.target.value.toLowerCase()),
    []
  );
  const onClickRequest = (requestQuote: Request) => {
    setSelectedRequest(requestQuote);
    setOpenSlider(true);
  };

  React.useEffect(() => {
    if (selectedRequest) {
      setRequestQuoteDetails(selectedRequest);
      setOpenSlider(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRequest]);

  const showQuoteAvailable = (documents: RequestDocument[], type: ERequestType, dispatcher: Dispatcher) => {
    if (type !== ERequestType.Mad) {
      return documents.length > 0 ? requestActions : requestDefaultActions;
    }
    if (type === ERequestType.Mad && dispatcher === null) {
      return requestDefaultActions;
    }
    if (type === ERequestType.Mad && dispatcher) {
      return dispatcher.price !== null ? requestActions : requestDefaultActions;
    }
    return requestDefaultActions;
  };

  const RequestAction = {
    ACCEPT: {
      label: text.all.accept,
    },
    DECLINE: {
      label: text.all.decline,
    },
    DELETE: {
      label: text.all.delete,
    },
    EDIT: {
      label: text.all.edit,
    },
  };

  const RequestActionByStatus = {
    [ERequestStatus.Accepted]: [RequestAction.DECLINE, RequestAction.DELETE],
    [ERequestStatus.Requested]: [RequestAction.EDIT, RequestAction.DELETE, RequestAction.ACCEPT, RequestAction.DECLINE],
  };

  const isSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const renderActionButton = useMemo(
    () => ({
      field: "Actions",
      title: "Actions",
      render: (selectedRequest: Request) => {
        const actions = () => {
          const __actions = [];
          if (
            selectedRequest?.status === ERequestStatus.Requested &&
            selectedRequest?.dispatcher?.price !== null &&
            selectedRequest?.documents.length !== 0 &&
            selectedRequest?.type === ERequestType.Mad
          ) {
            __actions.push(...RequestActionByStatus.REQUESTED);
          }
          if (
            (selectedRequest?.status === ERequestStatus.Requested &&
              selectedRequest?.type === ERequestType.Mad &&
              selectedRequest?.dispatcher === null) ||
            selectedRequest?.dispatcher?.price === null
          ) {
            __actions.push(...[RequestAction.EDIT, RequestAction.DELETE]);
          }
          if (
            selectedRequest?.status === ERequestStatus.Requested &&
            selectedRequest?.type === ERequestType.Tr &&
            selectedRequest?.templateAxes[0]?.discount === null &&
            selectedRequest?.documents.length === 0
          ) {
            __actions.push(...[RequestAction.EDIT, RequestAction.DELETE]);
          }
          if (
            selectedRequest?.status === ERequestStatus.Requested &&
            selectedRequest?.templateAxes[0]?.discount !== null &&
            selectedRequest?.type === ERequestType.Tr
          ) {
            __actions.push(...RequestActionByStatus.REQUESTED);
          }
          if (selectedRequest?.status === ERequestStatus.Requested && selectedRequest?.type === ERequestType.Nt) {
            __actions.push(...[RequestAction.EDIT, RequestAction.DELETE]);
          }
          if (selectedRequest?.status === ERequestStatus.Accepted && selectedRequest?.type === ERequestType.Nt) {
            __actions.push(...[RequestAction.EDIT]);
          }
          if (
            selectedRequest?.status === ERequestStatus.Requested &&
            selectedRequest?.documents.length > 0 &&
            selectedRequest?.type === ERequestType.Nt
          ) {
            __actions.push(...[RequestAction.ACCEPT, RequestAction.DECLINE]);
          }
          if (selectedRequest?.status === ERequestStatus.Accepted) {
            __actions.push(...RequestActionByStatus.ACCEPTED);
          }
          return __actions.map((action) => {
            return {
              key: action.label,
              label: action.label,
            };
          });
        };
        if (selectedRequest?.status === ERequestStatus.Approved) {
          return null;
        }

        if (
          selectedRequest?.status === ERequestStatus.Requested ||
          selectedRequest?.status === ERequestStatus.Accepted
        ) {
          return <MenuActions actions={actions()} row={selectedRequest} onRowMenuClick={onRowMenuClick} menuButton />;
        }
      },
    }),
    []
  );

  const renderRequest = () => (
    <>
      <Table
        searchTerm={searchTerm}
        tableHeight={isSmallScreen ? "43vh" : "58vh"}
        data={allRequests}
        isLoading={loading}
        rowsIdentifier="uid"
        columns={[...columns, renderActionButton]}
        onRowClick={onClickRequest}
        isCurrentRow={isCurrentRow}
        rowsPerPageOptions={ROW_PER_PAGE_OPTIONS}
        onChangePage={onChangePage}
        rowsPerPage={rowsPerPage}
        currentPage={page}
        countTotal={countTotal}
        setRowsPerPage={setRowsPerPage}
      />
      {isSmallScreen && (
        <Slider openDrawer={openSlider} handleClose={() => setOpenSlider(false)}>
          <RightSideDetailsRequest />
        </Slider>
      )}
    </>
  );

  const renderHeader = () => (
    <Grid container className={styles.root}>
      <Grid item xs={12} sm={6} md={6}>
        <Typography className={styles.title}>{text.all.request_follow_up}</Typography>
      </Grid>
      <Grid item xs={12} sm={6} md={6} style={{ display: "flex", marginTop: "10px" }}>
        <div className={styles.search}>
          <SearchBox onChange={handleSearchTerm} value={searchTerm} className={styles.search} />
        </div>
        <div className={styles.filterStyled}>
          <RequestFilter getDateFilter={getDateFilter} getFilter={getFilter} />
        </div>
      </Grid>
    </Grid>
  );

  return (
    <RequestListContainer>
      {renderHeader()}
      {allRequests?.length ? (
        renderRequest()
      ) : (
        <EmptyList>
          <EmptyTableContent />
        </EmptyList>
      )}

      <Dialog
        open={confirm}
        confirmButtonTitle={text.all.confirm}
        cancelButtonTitle={text.all.cancel}
        maxWidth="xs"
        hideDialogHeader
        dialogActionButtonCustomized
        className={classes.dialog}
      >
        <div className={classes.circle}>
          <Typography className={classes.helperIcon}>i</Typography>
        </div>
        <Grid container spacing={5}>
          <Grid item xs={12} className={classes.container}>
            <p className={classes.message}>
              {isAccept ? (
                text.all.are_you_want_accept_request
              ) : (
                <>
                  <div className={classes.divMessage}>{text.all.are_you_want_decline_request}</div>
                  <TextField
                    fullWidth
                    label={text.all.reason}
                    name="reason"
                    value={rejectionData.reason}
                    onChange={handleInputChange}
                    multiline
                    rows={10}
                    variant="outlined"
                    InputProps={{
                      className: classes.textarea,
                    }}
                  />
                </>
              )}
            </p>
            <Button
              variant="contained"
              onClick={handleConfirmRequest}
              color="primary"
              className={classes.yesButton}
              size="large"
              disabled={!isAccept && !rejectionData.reason?.length}
            >
              {text.all.yes_i_confirm}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setConfirm(false);
              }}
              className={classes.cancelButton}
              size="large"
            >
              {isAccept ? text.all.no_cancel_acceptation : text.all.no_cancel_declining}
            </Button>
          </Grid>
        </Grid>
      </Dialog>
    </RequestListContainer>
  );
}
